/**
 * Extended Expert Review – slider with progress bar
 */
(function () {
  'use strict';

  function debounce(fn, delay) {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(null, args), delay);
    };
  }

  function getSlidesVisible(root) {
    const width = window.innerWidth;
    const custom = parseFloat(root.dataset.slidesVisible);
    if (width <= 767) return parseFloat(root.dataset.slidesVisibleMob) || 1.15;
    if (width <= 1199) return parseFloat(root.dataset.slidesVisibleTab) || 2;
    return Number.isFinite(custom) ? custom : 3;
  }

  function initSlider(root) {
    if (!root || root.dataset.eerReady === 'true') return;

    const track = root.querySelector('[data-eer-track]');
    const prevBtn = root.querySelector('[data-eer-prev]');
    const nextBtn = root.querySelector('[data-eer-next]');
    const progressFill = root.querySelector('[data-eer-progress]');
    const enableLoop = root.dataset.loop === 'true';

    if (!track) return;

    let originalCount = 0;
    let trackIndex = 0;
    let loopAnchored = false;
    let isAnimating = false;
    let didAnimate = false;
    let animTimer = null;

    function getOriginalSlides() {
      return Array.from(track.querySelectorAll('.eer-slide:not(.eer-slide--clone)'));
    }

    function getGap() {
      return parseFloat(getComputedStyle(track).gap) || 24;
    }

    function getSlideStep() {
      const slide = track.querySelector('.eer-slide');
      if (!slide) return 0;
      return slide.offsetWidth + getGap();
    }

    function getLogicalIndex() {
      if (originalCount < 1) return 0;
      if (!enableLoop || originalCount < 2) {
        return Math.max(0, Math.min(trackIndex, originalCount - 1));
      }
      const offset = trackIndex - originalCount;
      return ((offset % originalCount) + originalCount) % originalCount;
    }

    function getMaxIndex() {
      const visible = getSlidesVisible(root);
      return Math.max(0, originalCount - Math.floor(visible));
    }

    function clearAnimLock() {
      isAnimating = false;
      if (animTimer) {
        clearTimeout(animTimer);
        animTimer = null;
      }
    }

    function lockAnim() {
      isAnimating = true;
      if (animTimer) clearTimeout(animTimer);
      animTimer = setTimeout(clearAnimLock, 550);
    }

    function clearClones() {
      track.querySelectorAll('.eer-slide--clone').forEach((el) => el.remove());
    }

    function setNavVisibility(show) {
      if (prevBtn) prevBtn.hidden = !show;
      if (nextBtn) nextBtn.hidden = !show;
    }

    function updateProgress() {
      if (!progressFill) return;

      if (enableLoop && originalCount >= 2) {
        const logical = getLogicalIndex();
        const pct = originalCount <= 1 ? 100 : ((logical + 1) / originalCount) * 100;
        progressFill.style.width = `${Math.min(100, Math.max(0, pct))}%`;
        return;
      }

      const maxIndex = getMaxIndex();
      const pct = maxIndex <= 0 ? 100 : (trackIndex / maxIndex) * 100;
      progressFill.style.width = `${Math.min(100, Math.max(0, pct))}%`;
    }

    function updateNavState() {
      if (enableLoop && originalCount >= 2) {
        if (prevBtn) prevBtn.disabled = false;
        if (nextBtn) nextBtn.disabled = false;
        return;
      }

      const maxIndex = getMaxIndex();
      if (prevBtn) prevBtn.disabled = trackIndex <= 0;
      if (nextBtn) nextBtn.disabled = trackIndex >= maxIndex;
    }

    function applyTransform(index, animate) {
      const step = getSlideStep();
      if (!step) return;

      track.classList.toggle('is-resetting', !animate);
      track.style.transform = `translate3d(-${index * step}px, 0, 0)`;
      trackIndex = index;
      didAnimate = !!animate;

      if (!animate) {
        requestAnimationFrame(() => track.classList.remove('is-resetting'));
      }

      updateProgress();
      updateNavState();
    }

    function snapIfNeeded() {
      if (!enableLoop || originalCount < 2) return;
      if (trackIndex >= originalCount * 2) {
        applyTransform(trackIndex - originalCount, false);
      } else if (trackIndex < originalCount) {
        applyTransform(trackIndex + originalCount, false);
      }
    }

    function buildLoop() {
      clearClones();
      const originals = getOriginalSlides();
      originalCount = originals.length;

      const visibleDesktop = parseFloat(root.dataset.slidesVisible) || 3;
      root.style.setProperty('--eer-slides-visible', String(visibleDesktop));

      if (!enableLoop || originalCount < 2) {
        loopAnchored = false;
        trackIndex = Math.min(trackIndex, getMaxIndex());
        applyTransform(trackIndex, false);
        setNavVisibility(originalCount > 1 && getMaxIndex() > 0);
        updateProgress();
        updateNavState();
        return;
      }

      [...originals].reverse().forEach((slide) => {
        const clone = slide.cloneNode(true);
        clone.classList.add('eer-slide--clone');
        clone.setAttribute('aria-hidden', 'true');
        track.insertBefore(clone, track.firstChild);
      });

      originals.forEach((slide) => {
        const clone = slide.cloneNode(true);
        clone.classList.add('eer-slide--clone');
        clone.setAttribute('aria-hidden', 'true');
        track.appendChild(clone);
      });

      if (!loopAnchored) {
        trackIndex = originalCount;
        loopAnchored = true;
      } else {
        trackIndex = originalCount + getLogicalIndex();
      }

      applyTransform(trackIndex, false);
      setNavVisibility(true);
      updateNavState();
    }

    function onTransitionEnd(event) {
      if (event.target !== track || event.propertyName !== 'transform') return;
      if (!didAnimate) return;
      didAnimate = false;
      clearAnimLock();
      snapIfNeeded();
    }

    function move(direction) {
      if (isAnimating || originalCount < 1) return;

      if (enableLoop && originalCount >= 2) {
        lockAnim();
        applyTransform(trackIndex + direction, true);
        return;
      }

      const maxIndex = getMaxIndex();
      const next = Math.max(0, Math.min(trackIndex + direction, maxIndex));
      lockAnim();
      applyTransform(next, true);
    }

    function refresh() {
      const logical = getLogicalIndex();
      buildLoop();
      if (enableLoop && originalCount >= 2) {
        trackIndex = originalCount + logical;
      } else if (originalCount > 0) {
        trackIndex = Math.min(logical, getMaxIndex());
      }
      requestAnimationFrame(() => applyTransform(trackIndex, false));
    }

    track.addEventListener('transitionend', onTransitionEnd);
    if (prevBtn) prevBtn.addEventListener('click', () => move(-1));
    if (nextBtn) nextBtn.addEventListener('click', () => move(1));

    window.addEventListener('resize', debounce(refresh, 150));

    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(debounce(refresh, 150));
      const viewport = root.querySelector('[data-eer-viewport]');
      if (viewport) ro.observe(viewport);
    }

    root.dataset.eerReady = 'true';
    refresh();
  }

  function initAll() {
    document.querySelectorAll('[data-extended-expert-review]').forEach((root) => {
      root.dataset.eerReady = 'false';
      initSlider(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target.querySelector('[data-extended-expert-review]');
    if (root) {
      root.dataset.eerReady = 'false';
      initSlider(root);
    }
  });
})();
